// Adverbs — how to form them (-mente), where they go, and the high-frequency
// ones worth memorising whole. A grouped reference table sits at the bottom.
import { renderGuideSections } from "../ui/guideSections.js";

const COMMON = {
  Time: [
    ["agora", "now"], ["hoje", "today"], ["ontem", "yesterday"], ["amanhã", "tomorrow"],
    ["sempre", "always"], ["nunca", "never"], ["já", "already / now"], ["ainda", "still / yet"],
    ["cedo", "early"], ["tarde", "late"], ["depois", "later / then"], ["em breve", "soon"],
  ],
  Place: [
    ["aqui", "here"], ["aí", "there"], ["ali", "over there"], ["perto", "near"],
    ["longe", "far"], ["dentro", "inside"], ["fora", "outside"], ["acima", "up / above"],
    ["abaixo", "down / below"], ["à frente", "in front"], ["atrás", "behind"], ["à volta", "around"],
  ],
  Manner: [
    ["bem", "well"], ["mal", "badly"], ["assim", "like this / so"], ["devagar", "slowly"],
    ["depressa", "fast"], ["juntos", "together"], ["claro", "clearly / of course"], ["melhor", "better"],
  ],
  Quantity: [
    ["muito", "very"], ["imenso", "a lot"], ["pouco", "little"], ["bastante", "quite / enough"],
    ["demasiado", "too much"], ["mais", "more"], ["menos", "less"], ["quase", "almost"],
  ],
};

export function adverbsView(app) {
  const block = (title, rows) => `
    <div class="bg-white rounded-2xl border border-sage-100 p-5">
      <h3 class="text-lg font-bold text-sage-800 mb-3">${title}</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Portuguese</th><th class="py-2 px-3">English</th>
        </tr></thead>
        <tbody>
          ${rows
            .map(
              ([pt, en]) => `<tr class="border-b border-sage-50 last:border-0">
            <td class="py-2 pr-3 font-medium text-sage-800">${pt}</td>
            <td class="py-2 px-3 text-sage-700">${en}</td>
          </tr>`
            )
            .join("")}
        </tbody>
      </table></div>
    </div>`;

  const table = `
    <div class="grid sm:grid-cols-2 gap-4 mt-6">
      ${block("Time", COMMON.Time)}
      ${block("Place", COMMON.Place)}
      ${block("Manner", COMMON.Manner)}
      ${block("Quantity", COMMON.Quantity)}
    </div>`;

  renderGuideSections(
    app,
    "Adverbs",
    "Adverbs describe how, when, where, or to what degree something happens. Unlike adjectives, they never change form to agree with anything — one shape fits all.",
    [
      {
        title: "1. Forming adverbs with -mente",
        body: `
          <p class="text-sage-700 mb-3">Take the <b>feminine singular</b> of an adjective and add <b>-mente</b> (the equivalent of English <i>-ly</i>).</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5 mb-3">
            <li><i>rápida → <b>rapidamente</b></i> (quickly)</li>
            <li><i>lenta → <b>lentamente</b></i> (slowly)</li>
            <li><i>fácil → <b>facilmente</b></i> (easily — no -a to change)</li>
          </ul>
          <p class="text-sage-700 text-sm"><b>Two in a row?</b> Only the last keeps -mente: <i>fala clara e calmamente</i>. <b>Heads-up:</b> unlike the adjective, the <i>-mente</i> adverb <b>drops</b> any written accent — <i>rápido → rapidamente</i>, <i>só → somente</i>.</p>
        `,
      },
      {
        title: "2. Position",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li>Adverbs of <b>manner</b> usually follow the verb: <i>Conduz <b>devagar</b>.</i></li>
            <li>Adverbs modifying an <b>adjective/adverb</b> go before it: <i><b>muito</b> alto, <b>bastante</b> bem</i>.</li>
            <li><i>Muito</i> is invariable when it modifies an adjective or adverb (<i>muito rápido</i>, <i>muito bem</i>), but agrees in gender and number when it modifies a noun (<i>muitos livros</i>, <i>muita água</i>).</li>
          </ul>
        `,
      },
      {
        title: "3. Adverbial phrases (no -mente needed)",
        body: `
          <p class="text-sage-700 mb-2">Portuguese often prefers a short phrase over a long -mente word:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>com cuidado</i> = carefully · <i>com frequência</i> = frequently</li>
            <li><i>de repente</i> = suddenly · <i>por fim</i> = finally · <i>muitas vezes</i> = often</li>
            <li><i>de novo</i> = again · <i>de imediato</i> = right away</li>
          </ul>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
